// Generat automat din README.md - Enhanced cu funcționalități interactive avansate pentru inventar produse botez
import React, { useState, useEffect } from 'react';
import { Package, Plus, Search, AlertTriangle, CheckCircle, Truck, ShoppingCart, TrendingUp, BarChart3, Download, Eye, Edit, Trash2, RefreshCw, Star, Baby, Heart, Gift, Flame, Zap, X, Info } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData } from '../../utils/mockData';

// Toast Component pentru notificări
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: CheckCircle,
    error: AlertTriangle,
    warning: AlertTriangle,
    info: Info
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500', 
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  const Icon = icons[type];

  useEffect(() => {
    const timer = setTimeout(() => onClose(), 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-in fixed top-4 right-4 z-50 min-w-80`}>
      <Icon className="w-5 h-5" />
      <span>{message}</span>
      <button onClick={onClose} className="ml-auto">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

const OptimizareStocuri = () => {
  // State management
  const [inventory, setInventory] = useState(mockData.inventory);
  const [filteredInventory, setFilteredInventory] = useState(inventory);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('Toate');
  const [filterStatus, setFilterStatus] = useState('Toate');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toasts, showToast, removeToast } = useToast();

  // Form pentru adăugare produs nou
  const [newProductForm, setNewProductForm] = useState({
    product: '',
    category: 'Ingrediente torturi',
    quantity: 0,
    minStock: 5,
    location: '',
    price: '',
    supplier: '',
    expirationDate: '',
    notes: ''
  });

  // Form pentru restock
  const [restockForm, setRestockForm] = useState({
    quantity: 0,
    cost: '',
    supplier: '',
    notes: ''
  });

  // Effects
  useEffect(() => {
    let filtered = inventory;
    
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterCategory !== 'Toate') {
      filtered = filtered.filter(item => item.category === filterCategory);
    }
    
    if (filterStatus !== 'Toate') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }
    
    setFilteredInventory(filtered);
  }, [inventory, searchTerm, filterCategory, filterStatus]);

  // Handlers
  const handleAddProduct = async () => {
    if (!newProductForm.product || !newProductForm.quantity) {
      showToast('Te rog completează toate câmpurile obligatorii', 'error');
      return;
    }

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const newProduct = {
      id: inventory.length + 1,
      ...newProductForm,
      status: newProductForm.quantity <= newProductForm.minStock ? 'Stoc redus' : 'În stoc'
    };

    setInventory(prev => [...prev, newProduct]);
    setNewProductForm({
      product: '', category: 'Ingrediente torturi', quantity: 0, minStock: 5,
      location: '', price: '', supplier: '', expirationDate: '', notes: ''
    });
    
    setShowAddModal(false);
    setLoading(false);
    showToast(`Produsul ${newProduct.product} a fost adăugat cu succes!`, 'success');
  };

  const handleRestock = async () => {
    if (!selectedProduct || !restockForm.quantity) {
      showToast('Te rog completează cantitatea pentru reaprovizionare', 'error');
      return;
    }

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const updatedQuantity = selectedProduct.quantity + parseInt(restockForm.quantity);
    const newStatus = updatedQuantity <= selectedProduct.minStock ? 'Stoc redus' : 'În stoc';
    
    setInventory(prev =>
      prev.map(item =>
        item.id === selectedProduct.id
          ? { ...item, quantity: updatedQuantity, status: newStatus }
          : item
      )
    );
    
    setShowRestockModal(false);
    setRestockForm({ quantity: 0, cost: '', supplier: '', notes: '' });
    setLoading(false);
    showToast(`${selectedProduct.product} a fost reaprovizionat cu ${restockForm.quantity} unități!`, 'success');
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest produs din inventar?')) return;
    
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setInventory(prev => prev.filter(item => item.id !== productId));
    setLoading(false);
    showToast('Produsul a fost șters din inventar!', 'success');
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const handleStartRestock = (product) => {
    setSelectedProduct(product);
    setShowRestockModal(true);
  };

  const handleExportData = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    showToast('Inventarul a fost exportat în format Excel!', 'success');
    setLoading(false);
  };

  // Statistici calculate
  const stats = {
    totalProducts: inventory.length,
    inStock: inventory.filter(i => i.status === 'În stoc').length,
    lowStock: inventory.filter(i => i.status === 'Stoc redus').length,
    criticalStock: inventory.filter(i => i.status === 'Stoc critic').length,
    totalValue: inventory.reduce((acc, i) => acc + (parseFloat(i.price.replace(/[^\d]/g, '') || 0) * i.quantity), 0)
  };

  // Categorii disponibile
  const categories = ['Toate', ...new Set(inventory.map(item => item.category))];

  // Configurare coloane tabel
  const columns = [
    {
      header: 'Produs',
      key: 'product',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {row.category === 'Ingrediente torturi' && <Heart className="w-4 h-4 text-red-500" />}
            {row.category === 'Lumânări' && <Flame className="w-4 h-4 text-yellow-500" />}
            {row.category === 'Îmbrăcăminte' && <Zap className="w-4 h-4 text-blue-500" />}
            {row.category === 'Decorațiuni tort' && <Star className="w-4 h-4 text-purple-500" />}
            {row.category === 'Ambalaje' && <Package className="w-4 h-4 text-gray-500" />}
            {row.category === 'Materiale decorare' && <Gift className="w-4 h-4 text-pink-500" />}
            {value}
          </p>
          <p className="text-sm text-gray-500">{row.category}</p>
        </div>
      )
    },
    {
      header: 'Stoc',
      key: 'quantity',
      render: (value, row) => (
        <div>
          <p className={`font-semibold ${value <= row.minStock ? 'text-red-600' : 'text-green-600'}`}>
            {value} buc
          </p>
          <p className="text-sm text-gray-500">Min: {row.minStock}</p>
        </div>
      )
    },
    {
      header: 'Status',
      key: 'status',
      render: (value) => {
        const variant = 
          value === 'În stoc' ? 'success' :
          value === 'Stoc redus' ? 'warning' :
          value === 'Stoc critic' ? 'error' : 'default';
        return <Badge variant={variant}>{value}</Badge>;
      }
    },
    {
      header: 'Locație',
      key: 'location',
      render: (value) => (
        <p className="text-sm font-medium">{value}</p>
      )
    },
    {
      header: 'Preț',
      key: 'price',
      render: (value) => (
        <p className="font-semibold text-purple-600">{value}</p>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (value, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Vezi detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleStartRestock(row)}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Reaprovizionează"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteProduct(row.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Șterge"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Toast Container */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-500" />
            Optimizare Stocuri - Produse Botez
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gestionează inventarul pentru torturi, lumânări, hăinuțe și decorațiuni de botez
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={handleExportData}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
          
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Produs Nou
          </button>
        </div>
      </div>

      {/* Statistici */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card title="Total Produse" icon={Package} variant="stats">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.totalProducts}
          </div>
          <p className="text-sm text-gray-500 mt-1">În inventar</p>
        </Card>

        <Card title="În Stoc" icon={CheckCircle} variant="stats">
          <div className="text-3xl font-bold text-green-600">
            {stats.inStock}
          </div>
          <p className="text-sm text-gray-500 mt-1">Stoc suficient</p>
        </Card>

        <Card title="Stoc Redus" icon={AlertTriangle} variant="stats">
          <div className="text-3xl font-bold text-yellow-600">
            {stats.lowStock}
          </div>
          <p className="text-sm text-gray-500 mt-1">Necesită reaprovizionare</p>
        </Card>

        <Card title="Stoc Critic" icon={AlertTriangle} variant="stats">
          <div className="text-3xl font-bold text-red-600">
            {stats.criticalStock}
          </div>
          <p className="text-sm text-gray-500 mt-1">Urgent de reaprovizionat</p>
        </Card>

        <Card title="Valoare Totală" icon={TrendingUp} variant="stats">
          <div className="text-3xl font-bold text-purple-600">
            {new Intl.NumberFormat('ro-RO').format(stats.totalValue)} RON
          </div>
          <p className="text-sm text-gray-500 mt-1">Valoare inventar</p>
        </Card>
      </div>

      {/* Alerte stoc */}
      {stats.criticalStock > 0 && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Atenție! Produse cu stoc critic
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>Următoarele produse au stocul foarte scăzut și necesită reaprovizionare urgentă:</p>
                  <ul className="mt-1 ml-4 list-disc">
                    {inventory.filter(item => item.status === 'Stoc critic').map(item => (
                      <li key={item.id}>
                        <strong>{item.product}</strong> - doar {item.quantity} bucăți rămase
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filtre și căutare */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Caută produse după nume, categorie sau locație..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="Toate">Toate statusurile</option>
              <option value="În stoc">În stoc</option>
              <option value="Stoc redus">Stoc redus</option>
              <option value="Stoc critic">Stoc critic</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Tabel inventar */}
      <Card title={`Inventar Produse (${filteredInventory.length})`}>
        <Table 
          columns={columns} 
          data={filteredInventory}
        />
      </Card>

      {/* Modal Adăugare Produs */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Adaugă Produs Nou în Inventar"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Numele Produsului *
              </label>
              <input
                type="text"
                value={newProductForm.product}
                onChange={(e) => setNewProductForm({...newProductForm, product: e.target.value})}
                className="input-field"
                placeholder="ex: Fondant alb (kg)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Categoria *
              </label>
              <select
                value={newProductForm.category}
                onChange={(e) => setNewProductForm({...newProductForm, category: e.target.value})}
                className="input-field"
              >
                <option value="Ingrediente torturi">Ingrediente torturi</option>
                <option value="Lumânări">Lumânări</option>
                <option value="Îmbrăcăminte">Îmbrăcăminte</option>
                <option value="Decorațiuni tort">Decorațiuni tort</option>
                <option value="Ambalaje">Ambalaje</option>
                <option value="Materiale decorare">Materiale decorare</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Cantitatea *
              </label>
              <input
                type="number"
                min="0"
                value={newProductForm.quantity}
                onChange={(e) => setNewProductForm({...newProductForm, quantity: parseInt(e.target.value) || 0})}
                className="input-field"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Preț pe Unitate
              </label>
              <input
                type="text"
                value={newProductForm.price}
                onChange={(e) => setNewProductForm({...newProductForm, price: e.target.value})}
                className="input-field"
                placeholder="ex: 25 RON/kg"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowAddModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button
              onClick={handleAddProduct}
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Adaugă Produs
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Reaprovizionare */}
      <Modal
        isOpen={showRestockModal}
        onClose={() => setShowRestockModal(false)}
        title={`Reaprovizionare - ${selectedProduct?.product}`}
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
              Status Actual
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400">Stoc curent:</p>
                <p className="font-semibold">{selectedProduct?.quantity} bucăți</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400">Stoc minim:</p>
                <p className="font-semibold">{selectedProduct?.minStock} bucăți</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Cantitate de Adăugat *
            </label>
            <input
              type="number"
              min="1"
              value={restockForm.quantity}
              onChange={(e) => setRestockForm({...restockForm, quantity: parseInt(e.target.value) || 0})}
              className="input-field"
              placeholder="Cantitatea pentru reaprovizionare"
            />
          </div>

          {restockForm.quantity > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">
                Preview Stoc Nou
              </h4>
              <p className="text-sm text-green-800 dark:text-green-200">
                Stoc după reaprovizionare: <strong>{(selectedProduct?.quantity || 0) + restockForm.quantity} bucăți</strong>
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowRestockModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button
              onClick={handleRestock}
              disabled={loading || !restockForm.quantity}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Reaprovizionează
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Detalii Produs */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Detalii Produs - ${selectedProduct?.product}`}
        size="lg"
      >
        {selectedProduct && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 p-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-600 rounded-full flex items-center justify-center text-white">
                    {selectedProduct.category === 'Ingrediente torturi' && <Heart className="w-8 h-8" />}
                    {selectedProduct.category === 'Lumânări' && <Flame className="w-8 h-8" />}
                    {selectedProduct.category === 'Îmbrăcăminte' && <Zap className="w-8 h-8" />}
                    {selectedProduct.category === 'Decorațiuni tort' && <Star className="w-8 h-8" />}
                    {selectedProduct.category === 'Ambalaje' && <Package className="w-8 h-8" />}
                    {selectedProduct.category === 'Materiale decorare' && <Gift className="w-8 h-8" />}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedProduct.product}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedProduct.category}
                    </p>
                    <Badge variant={selectedProduct.status === 'În stoc' ? 'success' : selectedProduct.status === 'Stoc redus' ? 'warning' : 'error'}>
                      {selectedProduct.status}
                    </Badge>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-sm text-gray-500">Stoc disponibil</p>
                  <p className={`text-3xl font-bold ${
                    selectedProduct.quantity <= selectedProduct.minStock ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {selectedProduct.quantity}
                  </p>
                  <p className="text-sm text-gray-500">bucăți</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button 
                onClick={() => {
                  setShowDetailsModal(false);
                  handleStartRestock(selectedProduct);
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reaprovizionează
              </button>
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="btn-secondary"
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OptimizareStocuri;